import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";
import { Pill } from "@/components/shared/Pill";
import Pagination, { usePagination } from "@/components/shared/Pagination";

// ─── Action → libellé FR + couleur Pill ──────────────────────────────────────
const ACTION_META = {
  // legacy (stay.history)
  created:               { label: "Création",               tone: "emerald", emoji: "➕", entity: "stay" },
  modified:              { label: "Modification",           tone: "violet",  emoji: "✏️", entity: "stay" },
  deleted:               { label: "Suppression",            tone: "rose",    emoji: "🗑️", entity: "stay" },
  archived:              { label: "Archivage",              tone: "amber",   emoji: "📦", entity: "" },
  unarchived:            { label: "Désarchivage",           tone: "emerald", emoji: "📤", entity: "" },
  // owners
  owner_created:         { label: "Création propriétaire",  tone: "emerald", emoji: "➕", entity: "owner" },
  owner_updated:         { label: "Modif. propriétaire",    tone: "violet",  emoji: "✏️", entity: "owner" },
  owner_archived:        { label: "Archivage propriétaire", tone: "amber",   emoji: "📦", entity: "owner" },
  owner_unarchived:      { label: "Désarchivage propriétaire", tone: "emerald", emoji: "📤", entity: "owner" },
  // properties
  property_created:      { label: "Création logement",      tone: "emerald", emoji: "🏠", entity: "property" },
  property_updated:      { label: "Modif. logement",        tone: "violet",  emoji: "✏️", entity: "property" },
  property_archived:     { label: "Archivage logement",     tone: "amber",   emoji: "📦", entity: "property" },
  property_unarchived:   { label: "Désarchivage logement",  tone: "emerald", emoji: "📤", entity: "property" },
  // stays
  stay_created:          { label: "Séjour créé",            tone: "emerald", emoji: "➕", entity: "stay" },
  stay_updated:          { label: "Séjour modifié",         tone: "violet",  emoji: "✏️", entity: "stay" },
  stay_deleted:          { label: "Séjour supprimé",        tone: "rose",    emoji: "🗑️", entity: "stay" },
  // requests
  request_created:       { label: "Demande soumise par le propriétaire", tone: "violet", emoji: "📨", entity: "request" },
  request_approved:      { label: "Demande acceptée",       tone: "emerald", emoji: "✅", entity: "request" },
  request_accepted:      { label: "Demande acceptée",       tone: "emerald", emoji: "✅", entity: "request" },
  request_rejected:      { label: "Demande refusée",        tone: "rose",    emoji: "❌", entity: "request" },
  // invoices
  invoice_created:       { label: "Facture créée",          tone: "emerald", emoji: "📄", entity: "invoice" },
  invoice_paid:          { label: "Facture soldée",         tone: "emerald", emoji: "💰", entity: "invoice" },
  invoice_unpaid:        { label: "Facture non soldée",     tone: "amber",   emoji: "💸", entity: "invoice" },
  invoice_deleted:       { label: "Facture supprimée",      tone: "rose",    emoji: "🗑️", entity: "invoice" },
  // login
  owner_login:           { label: "Connexion propriétaire", tone: "slate",   emoji: "🔑", entity: "user" },
  admin_login:           { label: "Connexion admin",        tone: "slate",   emoji: "🔑", entity: "user" },
  // supplements
  surcharge_created:     { label: "Surcoût ménage créé",    tone: "violet",  emoji: "🧹", entity: "surcharge" },
  intervention_created:  { label: "Intervention créée",     tone: "amber",   emoji: "🔧", entity: "intervention" },
  // settings
  database_reset:        { label: "Base réinitialisée",     tone: "rose",    emoji: "💥", entity: "settings" },
  entity_settings_updated: { label: "Réglages entité modifiés", tone: "violet", emoji: "⚙️", entity: "entity_settings" },
  entity_logo_uploaded:  { label: "Logo entité téléchargé", tone: "violet",  emoji: "🖼️", entity: "entity_settings" },
  // invoicing
  invoice_generated:     { label: "Facture générée",        tone: "violet",  emoji: "📄", entity: "invoice" },
  invoice_reminder_sent: { label: "Relance facture envoyée",tone: "amber",   emoji: "📧", entity: "invoice" },
  manual_line_created:   { label: "Ligne manuelle ajoutée", tone: "emerald", emoji: "➕", entity: "manual_line" },
  manual_line_updated:   { label: "Ligne manuelle modifiée", tone: "amber",   emoji: "✏️", entity: "manual_line" },
  manual_line_deleted:   { label: "Ligne manuelle supprimée",tone: "rose",   emoji: "🗑️", entity: "manual_line" },
  // ical
  stay_confirmed_from_ical: { label: "Séjour iCal confirmé", tone: "emerald", emoji: "🔗", entity: "stay" },
  ical_source_added:     { label: "Source iCal ajoutée",    tone: "emerald", emoji: "🔗", entity: "owner" },
  ical_source_removed:   { label: "Source iCal supprimée",  tone: "rose",    emoji: "🔗", entity: "owner" },
  ical_sync_run:         { label: "Synchro iCal exécutée",  tone: "violet",  emoji: "🔄", entity: "owner" },
};

const ENTITY_LABELS = {
  stay: "Séjour",
  owner: "Propriétaire",
  property: "Logement",
  invoice: "Facture",
  request: "Demande",
  surcharge: "Surcoût",
  intervention: "Intervention",
  settings: "Réglages",
  entity_settings: "Entité de facturation",
  manual_line: "Ligne manuelle",
  test: "Test",
};

// Libellés humains des champs techniques pour l'affichage des champs modifiés
const FIELD_HUMAN_LABELS = {
  start_date: "Date d'arrivée",
  end_date: "Date de départ",
  stay_type: "Type de séjour",
  services: "Prestations",
  tenant: "Locataire",
  notes_planning: "Notes planning",
  property_id: "Logement",
  owner_id: "Propriétaire",
  amount: "Montant",
  amount_ttc: "Montant TTC",
  type: "Type",
  paid: "Soldée",
  is_free: "Gratuit",
  price_ttc: "Prix TTC",
  invoice_number: "N° facture",
  short_name: "Nom court",
  billing_email: "Email facturation",
  display_name: "Nom",
  request_type: "Type de demande",
  stay_id: "Séjour",
  description: "Description",
  name: "Nom",
  tagline: "Slogan",
  legal_form: "Forme juridique",
  siret: "SIRET",
  siren: "SIREN",
  tva_label: "Mention TVA",
  vat_rate: "Taux TVA",
  address1: "Adresse",
  address2: "Adresse (suite)",
  phone: "Téléphone",
  email: "Email",
  iban: "IBAN",
  bic: "BIC",
  payment_delay_days: "Délai de paiement",
  ical_source: "Plateforme iCal",
  had_conflict: "Conflit iCal",
  first_name: "Prénom",
  last_name: "Nom",
  logo_gridfs_id: "ID Logo",
  filename: "Nom du fichier",
  size: "Taille",
};

// Clés techniques → label FR (utilise FIELD_HUMAN_LABELS comme base)
const DETAIL_KEY_LABELS = {
  ...FIELD_HUMAN_LABELS,
  fields: "Champs modifiés",
  changes: "Champs modifiés",
  reason: "Motif",
  payload: "Détails",
  property_short_name: "Logement",
  owner_display: "Propriétaire",
  stay_start_date: "Date d'arrivée",
  stay_end_date: "Date de départ",
  tenant_name: "Locataire",
  admin_decision_note: "Note admin",
  decision: "Décision",
  is_free: "Gratuit",
  old_start_date: "Ancienne date d'arrivée",
  old_end_date: "Ancienne date de départ",
  new_start_date: "Nouvelle date d'arrivée",
  new_end_date: "Nouvelle date de départ",
};

// Clés déjà affichées dans le header de la modale détail — à exclure de DetailsPretty
const KEYS_SHOWN_IN_HEADER = new Set([
  "property_short_name", "owner_display",
  "stay_start_date", "stay_end_date", "tenant_name",
]);

// Clés consommées par le bloc spécial "Dates avant → après" — à exclure du rendu générique
const KEYS_DATES_CHANGE = new Set([
  "old_start_date", "old_end_date", "new_start_date", "new_end_date",
]);

// Types de demande qui sont TOUJOURS gratuits — masquer price_ttc / is_free dans le rendu générique
const FREE_REQUEST_TYPES = new Set(["modify", "cancel"]);

// Convertit un payload de demande (services) en chips lisibles
function humanizePayloadChips(payload) {
  if (!payload || typeof payload !== "object") return [];
  const items = [];
  if (payload.cleaning === true) items.push("✅ Ménage");
  if (typeof payload.linen_kits_simple === "number" && payload.linen_kits_simple > 0) {
    items.push(`🛏️ ${payload.linen_kits_simple} kit${payload.linen_kits_simple > 1 ? "s" : ""} simple${payload.linen_kits_simple > 1 ? "s" : ""}`);
  }
  if (typeof payload.linen_kits_double === "number" && payload.linen_kits_double > 0) {
    items.push(`🛏️ ${payload.linen_kits_double} kit${payload.linen_kits_double > 1 ? "s" : ""} double${payload.linen_kits_double > 1 ? "s" : ""}`);
  }
  if (typeof payload.beds_made_simple === "number" && payload.beds_made_simple > 0) {
    items.push(`🛏️ ${payload.beds_made_simple} lit${payload.beds_made_simple > 1 ? "s" : ""} fait${payload.beds_made_simple > 1 ? "s" : ""} simple${payload.beds_made_simple > 1 ? "s" : ""}`);
  }
  if (typeof payload.beds_made_double === "number" && payload.beds_made_double > 0) {
    items.push(`🛏️ ${payload.beds_made_double} lit${payload.beds_made_double > 1 ? "s" : ""} fait${payload.beds_made_double > 1 ? "s" : ""} double${payload.beds_made_double > 1 ? "s" : ""}`);
  }
  if (payload.hygiene_kit === true) items.push("🧴 Kit hygiène");
  if (payload.degreasing === true) items.push("🧽 Dégraissage");
  if (payload.welcome_basket && payload.welcome_basket !== "none") {
    items.push(`🧺 Panier ${payload.welcome_basket}`);
  }
  if (Array.isArray(payload.puericulture_items)) {
    for (const it of payload.puericulture_items) items.push(`👶 ${it}`);
  }
  if (typeof payload.requested_time === "string" && payload.requested_time) {
    items.push(`🕐 ${payload.requested_time}`);
  }
  if (typeof payload.notes_planning === "string" && payload.notes_planning.trim()) {
    items.push(`📝 ${payload.notes_planning.trim()}`);
  }
  return items;
}

const REQUEST_TYPE_LABELS = {
  modify: "Modification",
  cancel: "Annulation",
  early_arrival: "Arrivée anticipée",
  late_departure: "Départ tardif",
};

const SURCHARGE_TYPE_LABELS = {
  poubelle: "Poubelle non sortie",
  vaisselle: "Vaisselle sale",
  linge_pas_defait: "Linge pas défait",
  cheminee_pas_videe: "Cheminée pas vidée",
  autre: "Autre",
};

// Formate proprement une valeur selon la clé
function formatValue(key, val, maps) {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Oui" : "Non";
  if (key === "amount" || key === "amount_ttc" || key === "price_ttc") {
    return `${Number(val).toFixed(2)} €`;
  }
  if (key === "start_date" || key === "end_date") return formatDateEU(val);
  if (key === "property_id" && maps.properties[val]) return maps.properties[val];
  if (key === "owner_id" && maps.owners[val]) return maps.owners[val];
  if (key === "stay_type") return val === "tenant" ? "Locataire" : val === "owner" ? "Propriétaire" : val;
  if (key === "request_type") return REQUEST_TYPE_LABELS[val] || val;
  if (key === "type" && SURCHARGE_TYPE_LABELS[val]) return SURCHARGE_TYPE_LABELS[val];
  if (Array.isArray(val)) {
    if (val.length === 0) return "—";
    return val.map((v) => FIELD_HUMAN_LABELS[v] || DETAIL_KEY_LABELS[v] || v).join(", ");
  }
  if (typeof val === "object") {
    if (val.old !== undefined || val.new !== undefined) {
      return `${val.old ?? "—"} → ${val.new ?? "—"}`;
    }
    return JSON.stringify(val);
  }
  // Tronque les UUIDs nus
  const s = String(val);
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) {
    return s.slice(0, 8) + "…";
  }
  return s;
}

function DetailsPretty({ details, maps, excludeKeys }) {
  if (!details || (typeof details === "object" && Object.keys(details).length === 0)) {
    return <span className="italic text-slate-500">—</span>;
  }
  if (typeof details === "string") return <span>{details}</span>;
  if (details.reason && Object.keys(details).length === 1) {
    return <span className="text-slate-200">{details.reason}</span>;
  }
  const skip = new Set(excludeKeys || []);

  // ─── Bloc spécial "Dates avant → après" (audit d'une demande modify) ─
  const hasDateChange =
    details.old_start_date || details.old_end_date ||
    details.new_start_date || details.new_end_date;
  const dateChangeBlock = hasDateChange ? (
    <div
      key="__dates_change__"
      data-testid="admin-history-detail-dates-change"
      className="rounded-md border border-white/10 bg-white/[0.04] p-2"
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Dates avant → Dates après</div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="text-slate-300 line-through">
          {formatDateEU(details.old_start_date || "") || "—"} → {formatDateEU(details.old_end_date || "") || "—"}
        </span>
        <span className="text-slate-400">➜</span>
        <span className="text-emerald-300 font-medium">
          {formatDateEU(details.new_start_date || "") || "—"} → {formatDateEU(details.new_end_date || "") || "—"}
        </span>
      </div>
    </div>
  ) : null;
  if (dateChangeBlock) {
    KEYS_DATES_CHANGE.forEach((k) => skip.add(k));
  }

  // ─── Modification / Annulation : toujours gratuit — masquer price_ttc/is_free ─
  // (le bloc "Gratuit" est affiché en pill en dessous)
  const isFreeRequest = FREE_REQUEST_TYPES.has(details.request_type);
  if (isFreeRequest) {
    skip.add("price_ttc");
    skip.add("is_free");
  }

  const entries = Object.entries(details).filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== "");
  if (!dateChangeBlock && !isFreeRequest && entries.length === 0) {
    return <span className="italic text-slate-500">—</span>;
  }
  return (
    <div className="space-y-2">
      {dateChangeBlock}
      {isFreeRequest && (
        <div
          data-testid="admin-history-detail-free-badge"
          className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-200"
        >
          ✓ Gratuit
        </div>
      )}
      {entries.map(([key, val]) => {
        // Cas spécial : payload (demande) → chips lisibles
        if (key === "payload" && val && typeof val === "object") {
          const chips = humanizePayloadChips(val);
          if (chips.length === 0) return null;
          return (
            <div key={key} className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Services demandés</div>
              <div className="flex flex-wrap gap-1.5">
                {chips.map((chip, i) => (
                  <span key={i} className="inline-flex items-center rounded-full bg-white/[0.06] border border-white/10 px-2 py-0.5 text-xs text-slate-100">{chip}</span>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="inline-flex items-baseline gap-2 mr-4">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              {DETAIL_KEY_LABELS[key] || key.replace(/_/g, " ")}
            </span>
            <span className="text-slate-100">{formatValue(key, val, maps)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminHistory() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");
  const [actorRoleFilter, setActorRoleFilter] = useState("");
  const [eventCategoryFilter, setEventCategoryFilter] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [detailLog, setDetailLog] = useState(null);

  const maps = useMemo(() => ({
    properties: Object.fromEntries(properties.map((p) => [p.id, p.short_name || p.id])),
    owners: Object.fromEntries(owners.map((o) => [o.id, o.display_name || `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.billing_email || o.id])),
  }), [properties, owners]);

  // Pagination — reset à la page 1 quand les filtres OU la liste changent
  const filterKey = `${fromDate}|${toDate}|${propertyFilter}|${actorRoleFilter}|${eventCategoryFilter}|${logs.length}`;
  const { page, setPage, pageSize, setPageSize, paginated, total } = usePagination(logs, { defaultPageSize: 20, resetKey: filterKey });

  const _buildParams = () => {
    const params = { limit: 300 };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (propertyFilter) params.property_id = propertyFilter;
    if (actorRoleFilter) params.actor_role = actorRoleFilter;
    if (eventCategoryFilter) params.event_category = eventCategoryFilter;
    return params;
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/history", { params: _buildParams() });
      setLogs(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/history/export.csv", { params: _buildParams(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `historique_bdm_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Export CSV téléchargé", description: "L'historique filtré a été exporté." });
    } catch (e) {
      toast({ title: "Export impossible", description: formatError(e), variant: "destructive" });
    }
  };

  const loadRefs = async () => {
    try {
      const [pRes, oRes] = await Promise.all([
        api.get("/admin/properties", { params: { include_archived: true } }),
        api.get("/admin/owners", { params: { include_archived: true } }),
      ]);
      setProperties(pRes.data || []);
      setOwners(oRes.data || []);
    } catch (e) {
      console.warn("Failed to load admin history refs", e);
    }
  };

  useEffect(() => {
    loadRefs();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toutes les actions présentes dans les logs courants pour alimenter le filtre
  return (
    <div data-testid="admin-history" className="space-y-4">
      <Card data-testid="admin-history-filters-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-history-title" className="text-slate-50">Historique des modifications</CardTitle>
          <div className="text-sm text-slate-300">
            Journal d&apos;audit de toutes les actions effectuées sur la plateforme.
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="space-y-1">
              <Label>Date début</Label>
              <Input data-testid="admin-history-from-date" type="date" className="border-white/10 bg-white/[0.08]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Date fin</Label>
              <Input data-testid="admin-history-to-date" type="date" className="border-white/10 bg-white/[0.08]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Logement</Label>
              <select data-testid="admin-history-property-filter" className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
                <option value="">Tous les logements</option>
                {properties.map((p) => (<option key={p.id} value={p.id}>{p.short_name || p.id}</option>))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Acteur</Label>
              <select data-testid="admin-history-actor-filter" className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={actorRoleFilter} onChange={(e) => setActorRoleFilter(e.target.value)}>
                <option value="">Tous</option>
                <option value="admin">Admin BDM</option>
                <option value="owner">Propriétaire</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Type d&apos;événement</Label>
              <select data-testid="admin-history-category-filter" className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={eventCategoryFilter} onChange={(e) => setEventCategoryFilter(e.target.value)}>
                <option value="">Tous</option>
                <option value="request">Demande</option>
                <option value="stay">Séjour</option>
                <option value="invoice">Facture</option>
                <option value="manual_line">Ligne manuelle</option>
                <option value="login">Connexion</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>&nbsp;</Label>
              <div className="flex gap-2">
                <Button data-testid="admin-history-filter-button" className="flex-1 rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={loadHistory} disabled={loading}>
                  {loading ? "..." : "Filtrer"}
                </Button>
                <Button data-testid="admin-history-export-csv-button" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" onClick={exportCsv} title="Exporter en CSV (UTF-8 + BOM, ouvrable dans Excel)">
                  📥
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="admin-history-list-card" className="border-white/10 bg-white/[0.08]">
        <CardContent className="pt-6">
          <div className="rounded-xl border border-white/10 bg-white/[0.08] overflow-x-auto">
            <Table data-testid="admin-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Date/Heure</TableHead>
                  <TableHead className="w-[200px]">Utilisateur</TableHead>
                  <TableHead className="w-[200px]">Action</TableHead>
                  <TableHead className="w-[110px]">Type</TableHead>
                  <TableHead>Détails</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((log, idx) => {
                  const meta = ACTION_META[log.action] || { label: log.action, tone: "slate", emoji: "•" };
                  // Préfère timestamp_paris fourni par le backend, sinon convertit la donnée UTC localement
                  let ts = log.timestamp_paris || log.timestamp || "";
                  if (!log.timestamp_paris && log.timestamp) {
                    try {
                      const d = new Date(log.timestamp);
                      const opts = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" };
                      const parts = new Intl.DateTimeFormat("fr-FR", opts).formatToParts(d);
                      const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
                      const dateStr = `${m.day}/${m.month}/${m.year}`;
                      const timeStr = `${m.hour}:${m.minute}`;
                      return (
                        <TableRow data-testid={`admin-history-row-${idx}`} key={log.id || idx} className="align-top cursor-pointer hover:bg-white/[0.04]" onClick={() => setDetailLog(log)}>
                          <TableCell className="text-sm text-slate-200 whitespace-nowrap">
                            <div>{dateStr}</div>
                            <div className="text-xs text-slate-400">{timeStr}</div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-200 break-all">
                            <div className="font-medium text-slate-100">{log.user_display || log.user_email || "—"}</div>
                            {log.user_display && log.user_email && log.user_display !== log.user_email && (
                              <div className="text-xs text-slate-400">{log.user_email}</div>
                            )}
                          </TableCell>
                          <TableCell><Pill tone={meta.tone}><span className="mr-1">{meta.emoji}</span>{meta.label}</Pill></TableCell>
                          <TableCell className="text-sm text-slate-200">
                            {log.property_short_name || ENTITY_LABELS[log.entity_type] || log.entity_type || "—"}
                          </TableCell>
                          <TableCell className="text-xs text-slate-300 max-w-xl">
                            <DetailsPretty details={log.details} maps={maps} />
                          </TableCell>
                        </TableRow>
                      );
                    } catch (err) {
                      console.warn("Failed to parse timestamp", err);
                    }
                  }
                  const dateStr = ts ? formatDateEU(ts.split("T")[0]) : "—";
                  const timeStr = ts.split("T")[1]?.substring(0, 5) || "";
                  return (
                    <TableRow data-testid={`admin-history-row-${idx}`} key={log.id || idx} className="align-top cursor-pointer hover:bg-white/[0.04]" onClick={() => setDetailLog(log)}>
                      <TableCell className="text-sm text-slate-200 whitespace-nowrap">
                        <div>{dateStr}</div>
                        <div className="text-xs text-slate-400">{timeStr}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-200 break-all">
                        <div className="font-medium text-slate-100">{log.user_display || log.user_email || "—"}</div>
                        {log.user_display && log.user_email && log.user_display !== log.user_email && (
                          <div className="text-xs text-slate-400">{log.user_email}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Pill tone={meta.tone}>
                          <span className="mr-1">{meta.emoji}</span>{meta.label}
                        </Pill>
                      </TableCell>
                      <TableCell className="text-sm text-slate-200">
                        {log.property_short_name || ENTITY_LABELS[log.entity_type] || log.entity_type || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-300 max-w-xl">
                        <DetailsPretty details={log.details} maps={maps} />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!logs.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-slate-300">
                      Aucun historique trouvé.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testIdPrefix="admin-history-pagination"
          />
        </CardContent>
      </Card>

      {/* Modal détail au clic sur une ligne */}
      <Dialog open={!!detailLog} onOpenChange={(open) => { if (!open) setDetailLog(null); }}>
        <DialogContent data-testid="admin-history-detail-dialog" className="border-white/10 bg-[#0a1428] text-slate-50 sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Détail de l&apos;événement</DialogTitle>
          </DialogHeader>
          {detailLog && (() => {
            const meta = ACTION_META[detailLog.action] || { label: detailLog.action, tone: "slate", emoji: "•" };
            const tsLocal = (() => {
              const t = detailLog.timestamp_paris || detailLog.timestamp;
              if (!t) return "—";
              try {
                const d = new Date(detailLog.timestamp_paris ? t.replace(/[+-]\d{2}:\d{2}$/, "") : t);
                return new Intl.DateTimeFormat("fr-FR", {
                  dateStyle: "long", timeStyle: "medium",
                  timeZone: detailLog.timestamp_paris ? undefined : "Europe/Paris",
                }).format(d);
              } catch (err) { console.warn(err); return t; }
            })();
            return (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Pill tone={meta.tone}><span className="mr-1">{meta.emoji}</span>{meta.label}</Pill>
                </div>
                <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                  <dt className="text-slate-400">Date & heure</dt>
                  <dd data-testid="admin-history-detail-time" className="text-slate-100 font-medium">📅 {tsLocal} <span className="text-xs text-slate-400">(Europe/Paris)</span></dd>
                  <dt className="text-slate-400">Acteur</dt>
                  <dd className="text-slate-100">{detailLog.user_display || detailLog.user_email || "—"}{detailLog.user_role && <span className="ml-1 text-xs text-slate-400">({detailLog.user_role === "admin" ? "admin" : "propriétaire"})</span>}</dd>
                  {detailLog.property_short_name && <><dt className="text-slate-400">Logement</dt><dd className="text-slate-100">{detailLog.property_short_name}</dd></>}
                  {detailLog.owner_display && <><dt className="text-slate-400">Propriétaire</dt><dd className="text-slate-100">{detailLog.owner_display}</dd></>}
                  {(detailLog.details?.stay_start_date || detailLog.details?.stay_end_date) && (
                    <><dt className="text-slate-400">Séjour</dt><dd className="text-slate-100">
                      {detailLog.details.stay_start_date && formatDateEU(detailLog.details.stay_start_date)}
                      {" → "}
                      {detailLog.details.stay_end_date && formatDateEU(detailLog.details.stay_end_date)}
                      {detailLog.details.tenant_name && <span className="ml-2 text-slate-300">· {detailLog.details.tenant_name}</span>}
                    </dd></>
                  )}
                  <dt className="text-slate-400">Détails</dt>
                  <dd className="text-slate-200"><DetailsPretty details={detailLog.details} maps={maps} excludeKeys={KEYS_SHOWN_IN_HEADER} /></dd>
                </dl>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={() => setDetailLog(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
